import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const Navigation: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const t = translations[language];

  const prefix = language === 'fr' ? '' : `/${language}`;
  const homePath = prefix || '/';

  const isHome =
    location.pathname === '/' ||
    location.pathname === '/en' ||
    location.pathname === '/es';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    if (!isHome) {
      navigate(homePath);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const offset = 58;
          window.scrollTo({
            top: el.getBoundingClientRect().top + window.pageYOffset - offset,
            behavior: 'smooth',
          });
        }
      }, 150);
    } else {
      const el = document.getElementById(id);
      if (el) {
        const offset = 58;
        window.scrollTo({
          top: el.getBoundingClientRect().top + window.pageYOffset - offset,
          behavior: 'smooth',
        });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <button
        className="navbar-brand"
        onClick={() => navigate(homePath)}
        aria-label="Home"
      >
        <img src="/images/logo.png" className="navbar-logo" alt="Sparkr Logo" />
      </button>
      <button
        className="navbar-toggler"
        type="button"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Toggle navigation"
      >
        <span className="navbar-toggler-icon"></span>
      </button>
      <div className={`navbar-collapse ${isMenuOpen ? 'show' : ''}`}>
        <ul className="navbar-nav">
          <li className="nav-item">
            <button className="nav-link" onClick={() => scrollToSection('hero')}>
              {t.nav.home}
            </button>
          </li>
          <li className="nav-item">
            <button className="nav-link" onClick={() => scrollToSection('mission')}>
              {t.nav.mission}
            </button>
          </li>
          <li className="nav-item dropdown">
            <button className="nav-link" onClick={() => scrollToSection('offers')}>
              {t.nav.offers}
            </button>
            <ul className="dropdown-menu">
              {t.offerPages.map((offer) => (
                <li key={offer.id}>
                  <Link
                    to={`${prefix}/offers/${offer.slug}`}
                    className="dropdown-item"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {offer.title}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
          <li className="nav-item">
            <button className="nav-link" onClick={() => scrollToSection('clients')}>
              {t.nav.clients}
            </button>
          </li>
          <li className="nav-item">
            <button className="nav-link" onClick={() => scrollToSection('contact')}>
              {t.nav.contact}
            </button>
          </li>
          <li className="nav-item">
            <button className="nav-link" onClick={() => scrollToSection('about')}>
              {t.nav.about}
            </button>
          </li>
        </ul>
      </div>
      <div className="language-switcher nav-item dropdown">
        <button
          className="language-switcher-toggle"
          aria-expanded="false"
          aria-haspopup="true"
        >
          {language === 'fr' ? 'FR' : language === 'es' ? 'ES' : 'EN'}
        </button>
        <ul className="dropdown-menu">
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setLanguage('fr');
                setIsMenuOpen(false);
              }}
            >
              Français
            </button>
          </li>
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setLanguage('en');
                setIsMenuOpen(false);
              }}
            >
              English
            </button>
          </li>
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setLanguage('es');
                setIsMenuOpen(false);
              }}
            >
              Español
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
